/**
 * useFloorPlanCertConfig — the FPC module's ops hook (the ONLY write path).
 *
 * MODULE-CONTRACT §2. The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|floor_plan_certification"]`; this hook wraps it
 * with a typed read (field-merged over the default) and two writers. No imperative
 * handle, no serialize/hydrate — `serializeDraft` / `hydrateFromDraft` already
 * round-trip `productConfigs`.
 *
 * Both writers are inherently the USER-EDIT boundary — hydration never calls them
 * (§3) — so each un-confirms unconditionally via `markConfigDirty`.
 *
 * Replaces the legacy `addFloors`/`removeFloors` MINUS all cart-product
 * side-effects (architect-stamp + extra-certified-floor product selection) — those
 * wrote the legacy products context and are dead in v2 (see spec §5 / DF list).
 */

import { useCallback } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useFlowConfig } from '../useFlowConfig'
import {
  FLOOR_PLAN_CERT_DEFAULT,
  floorPlanCertIsComplete,
  type FloorPlanCertificationDraftDTO
} from './floorPlanCertification.dto'

const FLOW_ID = 'floor_plan_certification' as const

export interface UseFloorPlanCertConfig {
  /** Field-complete DTO — safe to read every TOP-LEVEL field, even against a
   *  partial or absent persisted entry (§2 partial-DTO invariant). NOTE: the merge
   *  is shallow, so `floors` is NOT deep-defaulted — index it defensively. */
  value: FloorPlanCertificationDraftDTO
  /** Merge-and-write a partial DTO (propertyType / areaCalc / comment / floors).
   *  Idempotency-guarded by `setConfig`; un-confirms the tab. */
  patch: (partial: Partial<FloorPlanCertificationDraftDTO>) => void
  /** Grow/shrink the `floors` array to length `n` (preserving existing names,
   *  reindexing `floorNumber = idx + 1`) and set `numberOfFloors = n` in one write. */
  setFloorCount: (n: number) => void
  isComplete: boolean
}

export function useFloorPlanCertConfig(cartItemId: CartItemId): UseFloorPlanCertConfig {
  const { value, patch, update, isComplete } = useFlowConfig(cartItemId, FLOW_ID, FLOOR_PLAN_CERT_DEFAULT, floorPlanCertIsComplete)

  const setFloorCount = useCallback((n: number) => update((current) => {
    const count = Math.max(0, n)
    const existing = current.floors ?? []
    // Rebuild to exactly `count` floors, preserving names and reindexing floorNumber.
    const floors = Array.from({ length: count }, (_, idx) => ({ name: existing[idx]?.name ?? '', floorNumber: idx + 1 }))
    return { numberOfFloors: count, floors }
  }), [update])

  return { value, patch, setFloorCount, isComplete }
}
